import { EConnectionEvents, EWebsocketClientEvents, TAllEvents, TWebSocketMessage } from 'webrtc/events';
import { ISignalClient, TListener, TRawConnection, TSignalCallback } from './types';
import logger from 'utils/log';

// 2.5 minutes — keeps ZScaler (if applicable, 5-min idle WebSocket timeout) and API Gateway (10-min) alive
export const HEARTBEAT_INTERVAL_MS = 150_000;
export const MAX_RECONNECT_ATTEMPTS = 10;
const BASE_RECONNECT_DELAY_MS = 1_000;
// AVS-4302 takeover reliability: bound for signals buffered while the socket is CONNECTING —
// a handshake that never completes must not grow the queue unboundedly.
export const MAX_QUEUED_SIGNALS = 50;

/** Which way a frame crossed the socket. */
export type TSignalDirection = 'out' | 'in';

/** Everything the sink learns about a frame beyond the frame itself. */
export interface ISignalFrameMeta {
  /** Which of the three sockets it crossed. */
  url: string;
  /** The socket's `readyState` on the way OUT, so a frame dropped by a closed socket is visible. */
  readyState?: number;
  /** The untouched wire text of an inbound frame, when the event carried a string. */
  raw?: string;
  /**
   * Set only when `JSON.parse` threw, in which case `frame` is `undefined` and `raw` is all there
   * is. A malformed frame still throws out of `onmessage` exactly as it did before the sink
   * existed — the recorder observes, it never changes behaviour — but it is now observed.
   */
  parseError?: string;
  /**
   * True when this outbound frame is the QUEUED one going out again.
   *
   * `flushOutboundQueue` re-enters `signal()`, so a frame buffered while the socket was CONNECTING
   * is legitimately reported twice, and without this the two are indistinguishable — anyone
   * counting outbound offers double-counts every one that was ever queued.
   */
  replay?: boolean;
}

/**
 * Sink for the Session Flight Recorder (AVS-4426), consumed by AVS-4431.
 *
 * `frame` is the parsed message, or `undefined` when the wire text could not be parsed at all —
 * see {@link ISignalFrameMeta.parseError}.
 */
export type TSignalFrameSink = (direction: TSignalDirection, frame: unknown, meta: ISignalFrameMeta) => void;

function noopSignalFrameSink(): void {
  // Default sink: no recorder installed.
}

let signalFrameSink: TSignalFrameSink = noopSignalFrameSink;

/**
 * Register a sink for every signaling frame. Pass `null` to restore the no-op.
 *
 * **MODULE scope, not per-instance, and that is the whole point.** A new `SignalerClient` is
 * constructed on every `connect()` (`webrtc/index.ts:174-175`) and `utils/ui/rtc.ts:26` reassigns
 * the module binding outright on a VOIP takeover — so an instance-level hook goes blind at exactly
 * the moment the outage it exists to record begins.
 *
 * Same discipline as `setListenerGuardReporter` (`utils/events.ts:61`): a settable module-level
 * function defaulting to a no-op, wrapped in its own try/catch so a throwing sink can never drop a
 * signal or break a handshake.
 */
export function setSignalFrameSink(sink: TSignalFrameSink | null): void {
  signalFrameSink = sink ?? noopSignalFrameSink;
}

/** One isolation point for all three call sites — a throwing sink must never cost a frame. */
function emitSignalFrame(direction: TSignalDirection, frame: unknown, meta: ISignalFrameMeta): void {
  try {
    signalFrameSink(direction, frame, meta);
  } catch (error) {
    logger.warn('[SignalerClient] frame sink threw and was isolated', error);
  }
}

/**
 * A signaling occurrence that is **not** a frame: a queue drop, a socket lifecycle transition or a
 * raw-handler swap.
 *
 * Deliberately a second sink rather than a widened {@link TSignalFrameSink}. The frame sink's
 * signature is frozen by the PR-3 recorder contract, and a drop is not a frame — `signal()` calls
 * the frame sink *before* the `readyState` branch (so a frame lost to a dead socket is recorded at
 * all), which means the frame sink alone cannot say whether the frame was sent, buffered or
 * discarded. A `CONNECTING` socket with a full queue and a `CONNECTING` socket with room look
 * identical from `readyState`, and only one of them loses the frame.
 */
export type TSignalEvent =
  /** Buffered while the socket is CONNECTING; it will be re-sent by `flushOutboundQueue`. */
  | 'queued'
  /** Discarded: the outbound queue was already at {@link MAX_QUEUED_SIGNALS}. */
  | 'queue-full-drop'
  /** Discarded: the socket was CLOSING or CLOSED. */
  | 'socket-closed-drop'
  /** Discarded: `connect()` has never run, so there is no socket at all. */
  | 'no-socket-drop'
  /** The buffered frames are being re-sent after a completed handshake. */
  | 'queue-flush'
  /** The whole buffer was discarded (intentional disconnect, or a reconnect peer-state reset). */
  | 'queue-dropped'
  | 'socket-open'
  | 'socket-closed'
  | 'socket-error'
  /** `setRawListeners()` (re-)assigned the raw handlers — the swap itself is evidence. */
  | 'raw-listeners-set'
  /** One scheduled reconnect attempt, carrying its backoff delay. */
  | 'reconnect-scheduled'
  /** A reconnect attempt is opening its own socket, with its own handshake `onmessage`. */
  | 'reconnect-attempt'
  /** The reconnect handshake completed under a new connectionId. */
  | 'reconnected'
  /** The reconnect loop exhausted {@link MAX_RECONNECT_ATTEMPTS}. Once per outage. */
  | 'reconnect-gave-up'
  /** An inbound frame was not JSON. The parse error is re-thrown unchanged after this fires. */
  | 'parse-failed'
  | 'connect'
  | 'disconnect';

/** Sink for {@link TSignalEvent}. `detail.url` names which of the three sockets it belongs to. */
export type TSignalEventSink = (event: TSignalEvent, detail: { url: string } & Record<string, unknown>) => void;

function noopSignalEventSink(): void {
  // Default sink: no recorder installed.
}

let signalEventSink: TSignalEventSink = noopSignalEventSink;

/**
 * Register a sink for the non-frame signaling events. Pass `null` to restore the no-op.
 *
 * Module scope for the same reason as {@link setSignalFrameSink}, and that reason bites hardest
 * here: the events worth having — the reconnect ladder and the queue drops — all happen while the
 * client instance is being replaced.
 */
export function setSignalEventSink(sink: TSignalEventSink | null): void {
  signalEventSink = sink ?? noopSignalEventSink;
}

/**
 * The shape an inbound frame is assumed to have once parsed.
 *
 * Asserted, not validated: the wire is the wire, and this only restates what `JSON.parse` already
 * returned as `any` before. It exists so no `any` has to.
 */
interface IInboundFrame {
  action: TAllEvents;
  from?: string;
  data: Record<string, unknown>;
}

/** One isolation point for every event call site — a throwing sink must never cost a reconnect. */
function emitSignalEvent(event: TSignalEvent, detail: { url: string } & Record<string, unknown>): void {
  try {
    signalEventSink(event, detail);
  } catch (error) {
    logger.warn('[SignalerClient] event sink threw and was isolated', error);
  }
}

export class SignalerClient implements ISignalClient {
  public wsApiUrl: string;

  private wsClient!: WebSocket;

  private listeners: TListener[];

  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;

  private isIntentionalDisconnect: boolean = false;

  private reconnectAttempts: number = 0;

  // Latches once the reconnect loop gives up, so WsClientGaveUp fires exactly once per outage.
  // Reset on any successful (re)connect so a later outage can signal again.
  private hasGivenUp: boolean = false;

  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private outboundQueue: TWebSocketMessage[] = [];

  // Observation only: it marks the frames `flushOutboundQueue` sends back through `signal()`, and
  // nothing in the send path reads it.
  private isFlushingQueue: boolean = false;

  constructor(wsApiUrl: string) {
    this.wsApiUrl = wsApiUrl;
    this.listeners = [];
  }

  public signal(message: TWebSocketMessage) {
    // On the way out, before the readyState branch: a frame we DROP because the socket is closed
    // is exactly the evidence a signaling outage leaves behind, and only this position records it.
    // `readyState` distinguishes the first, queued attempt from the flushed one — `flushOutboundQueue`
    // re-enters `signal()`, so a buffered frame is legitimately recorded twice.
    emitSignalFrame('out', message, {
      url: this.wsApiUrl,
      readyState: this.wsClient?.readyState,
      replay: this.isFlushingQueue,
    });

    if (!this.wsClient) {
      emitSignalEvent('no-socket-drop', { url: this.wsApiUrl, action: message.action });
      return;
    }

    if (this.wsClient.readyState === WebSocket.OPEN) {
      this.wsClient.send(JSON.stringify(message));
    } else if (this.wsClient.readyState === WebSocket.CONNECTING) {
      // AVS-4302 takeover reliability: NEVER assign wsClient.onopen here — overwriting it
      // detaches the raw open listener from setRawListeners, which both drops every previously
      // "queued" message but the last AND kills the handshake (HandshakeStart is sent from that
      // listener). Buffer instead; the queue flushes once the handshake completes — sending
      // earlier would race server-side connection registration.
      if (this.outboundQueue.length >= MAX_QUEUED_SIGNALS) {
        logger.warn('[SignalerClient] Outbound queue full — dropping signal', message.action);
        emitSignalEvent('queue-full-drop', {
          url: this.wsApiUrl,
          action: message.action,
          queued: this.outboundQueue.length,
        });
        return;
      }
      this.outboundQueue.push(message);
      emitSignalEvent('queued', { url: this.wsApiUrl, action: message.action, queued: this.outboundQueue.length });
    } else {
      logger.warn('[SignalerClient] Socket closed — dropping signal', message.action);
      emitSignalEvent('socket-closed-drop', {
        url: this.wsApiUrl,
        action: message.action,
        readyState: this.wsClient.readyState,
      });
    }
  }

  private flushOutboundQueue(): void {
    if (!this.outboundQueue.length) return;
    const queued = this.outboundQueue;
    this.outboundQueue = [];
    emitSignalEvent('queue-flush', { url: this.wsApiUrl, count: queued.length });
    // Marked while re-entering `signal()`, so the recorder can tell "buffered" from "sent" instead
    // of showing the same offer twice as `dir: 'out'`.
    this.isFlushingQueue = true;
    try {
      queued.forEach((message) => this.signal(message));
    } finally {
      this.isFlushingQueue = false;
    }
  }

  private dropOutboundQueue(reason: string): void {
    if (!this.outboundQueue.length) return;
    logger.warn(`[SignalerClient] Dropping ${this.outboundQueue.length} queued signal(s): ${reason}`);
    emitSignalEvent('queue-dropped', {
      url: this.wsApiUrl,
      count: this.outboundQueue.length,
      reason,
      actions: this.outboundQueue.map((message) => message.action),
    });
    this.outboundQueue = [];
  }

  public async connect(): Promise<TRawConnection> {
    this.disconnect();
    this.isIntentionalDisconnect = false;
    this.reconnectAttempts = 0;
    this.hasGivenUp = false;
    emitSignalEvent('connect', { url: this.wsApiUrl });
    return new Promise((res, rej) => {
      this.wsClient = new WebSocket(this.wsApiUrl);
      this.setRawListeners();

      // Capture connect()-scoped callbacks so cleanup() only removes these
      // specific registrations and never touches application-level listeners.
      const onOpen = () => {
        this.signal({ action: EConnectionEvents.HandshakeStart, data: {} });
      };

      const onHandshakeComplete = (connection: TRawConnection) => {
        cleanup();
        this.startHeartbeat();
        this.flushOutboundQueue();
        res(connection);
      };

      const onError = (e: unknown) => {
        cleanup();
        rej(e);
      };

      const cleanup = () => {
        this.off(EWebsocketClientEvents.WsClientOpen, onOpen);
        this.off(EConnectionEvents.HandshakeComplete, onHandshakeComplete);
        this.off(EWebsocketClientEvents.WsClientError, onError);
      };

      this.on(EWebsocketClientEvents.WsClientOpen, onOpen);
      this.on(EConnectionEvents.HandshakeComplete, onHandshakeComplete);
      this.on(EWebsocketClientEvents.WsClientError, onError);
    });
  }

  public on(event: TAllEvents, callback: TSignalCallback) {
    this.listeners.push({ event, callback });
  }

  public off(event: TAllEvents, callback?: TSignalCallback) {
    if (callback) {
      this.listeners = this.listeners.filter(({ event: e, callback: cb }) => !(e === event && cb === callback));
    } else {
      this.listeners = this.listeners.filter(({ event: e }) => e !== event);
    }
  }

  public disconnect() {
    // Fires on an explicit teardown AND at the head of every connect(), which calls this first.
    // A `disconnect` immediately followed by a `connect` on the same url is a reconnection, not a
    // session ending — the timeline reads correctly only if that is stated somewhere.
    emitSignalEvent('disconnect', { url: this.wsApiUrl });
    this.isIntentionalDisconnect = true;
    this.stopHeartbeat();
    this.clearReconnectTimer();
    this.dropOutboundQueue('intentional disconnect');
    if (!this.wsClient) {
      return;
    }
    if (this.isReady()) {
      this.wsClient.close();
      this.listeners = [];
    }
  }

  private setRawListeners = () => {
    // The swap itself is evidence: this runs on every connect() AND again after every reconnect
    // handshake, which is precisely why an instance-property wrap of onmessage goes blind mid-outage.
    emitSignalEvent('raw-listeners-set', { url: this.wsApiUrl, readyState: this.wsClient?.readyState });

    this.wsClient.onopen = () => {
      emitSignalEvent('socket-open', { url: this.wsApiUrl, phase: 'primary' });
      this.callListener(EWebsocketClientEvents.WsClientOpen);
    };

    this.wsClient.onclose = () => {
      emitSignalEvent('socket-closed', {
        url: this.wsApiUrl,
        phase: 'primary',
        intentional: this.isIntentionalDisconnect,
      });
      this.callListener(EWebsocketClientEvents.WsClientClosed);
      if (!this.isIntentionalDisconnect) {
        this.stopHeartbeat();
        this.scheduleReconnect();
      }
    };

    this.wsClient.onerror = () => {
      emitSignalEvent('socket-error', { url: this.wsApiUrl, phase: 'primary' });
      this.callListener(EWebsocketClientEvents.WsClientError);
    };

    this.wsClient.onmessage = (event) => {
      const parsed = this.parseInbound(event.data, 'primary');
      emitSignalFrame('in', parsed, {
        url: this.wsApiUrl,
        raw: typeof event.data === 'string' ? event.data : undefined,
      });
      const { action, from, data } = parsed;
      this.callListener(action, data, from);
    };
  };

  /**
   * `JSON.parse` with a capture point in front of the throw.
   *
   * The error is **re-thrown unchanged** — swallowing it here would turn a malformed frame into a
   * silently ignored one, which is the exact class of evidence loss this instrumentation exists to
   * end. The only change is that the recorder now sees the frame that could not be parsed.
   */
  private parseInbound(data: unknown, phase: 'primary' | 'reconnect'): IInboundFrame {
    try {
      return JSON.parse(data as string) as IInboundFrame;
    } catch (error) {
      emitSignalEvent('parse-failed', {
        url: this.wsApiUrl,
        phase,
        length: typeof data === 'string' ? data.length : undefined,
        error: String(error),
      });
      // BOTH sinks, deliberately. The event sink carries the richer diagnostic (phase, length),
      // but `TSignalFrameSink` documents `meta.raw` as "the untouched wire text for an inbound
      // frame that failed to parse" — and until this line that was the one case it never saw, so
      // `raw` was only ever set for frames that parsed fine. A frame consumer must not have to
      // subscribe to the event sink as well to learn that a frame was lost.
      emitSignalFrame('in', undefined, {
        url: this.wsApiUrl,
        raw: typeof data === 'string' ? data : undefined,
        parseError: String(error),
      });
      // Rethrown unchanged: the recorder observes, it never intervenes. A malformed frame still
      // throws out of `onmessage` exactly as it did before either sink existed.
      throw error;
    }
  }

  private callListener(event: TAllEvents, data?: Record<string, unknown>, from?: string) {
    this.listeners.forEach((listener) => {
      if (listener.event === event) listener.callback(data, from);
    });
  }

  public isReady() {
    return this.wsClient.readyState === WebSocket.CONNECTING || this.wsClient.readyState === WebSocket.OPEN;
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      this.signal({ action: EConnectionEvents.Ping, data: {} });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      logger.error('[SignalerClient] Max reconnect attempts reached, giving up');
      // Signal degraded mode once so the session layer can fall back to slow-poll read-repair
      // (AVS-4330). Latched: further onclose events in this dead state must not re-fire it.
      if (!this.hasGivenUp) {
        this.hasGivenUp = true;
        emitSignalEvent('reconnect-gave-up', { url: this.wsApiUrl, attempts: this.reconnectAttempts });
        this.callListener(EWebsocketClientEvents.WsClientGaveUp);
      }
      return;
    }

    const delay = Math.min(BASE_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempts, 30_000);
    this.reconnectAttempts++;
    // AVS-4332: emitted AFTER the increment and BELOW the MAX guard above, so a full outage yields
    // exactly MAX_RECONNECT_ATTEMPTS events (emitting above the guard would yield MAX + 1).
    emitSignalEvent('reconnect-scheduled', {
      url: this.wsApiUrl,
      attempt: this.reconnectAttempts,
      maxAttempts: MAX_RECONNECT_ATTEMPTS,
      delay,
    });
    this.callListener(EWebsocketClientEvents.WsClientReconnecting);
    logger.warn(
      `[SignalerClient] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`
    );
    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null;
      this.doReconnect();
    }, delay);
  }

  /**
   * Re-establishes the WebSocket after an unexpected disconnect.
   *
   * Uses a dedicated onmessage handler during the handshake phase so that
   * one-shot setup listeners from the initial connect() call are not reused.
   * After HandshakeComplete the normal setRawListeners() path takes over and
   * WsClientReconnected is dispatched so RTCClient can reset peer state.
   */
  private doReconnect(): void {
    if (this.isIntentionalDisconnect) return;

    emitSignalEvent('reconnect-attempt', { url: this.wsApiUrl, attempt: this.reconnectAttempts });

    const ws = new WebSocket(this.wsApiUrl);

    ws.onopen = () => {
      emitSignalEvent('socket-open', { url: this.wsApiUrl, phase: 'reconnect' });
      const handshake = { action: EConnectionEvents.HandshakeStart, data: {} };
      // This handshake bypasses signal() entirely, so without an explicit call it is the one
      // outbound frame the recorder would never see — and it opens every reconnect.
      emitSignalFrame('out', handshake, { url: this.wsApiUrl, readyState: ws.readyState });
      ws.send(JSON.stringify(handshake));
    };

    ws.onclose = () => {
      emitSignalEvent('socket-closed', {
        url: this.wsApiUrl,
        phase: 'reconnect',
        intentional: this.isIntentionalDisconnect,
      });
      // Failed during reconnect handshake — back off and try again
      if (!this.isIntentionalDisconnect) {
        this.scheduleReconnect();
      }
    };

    ws.onerror = () => {
      // onerror always precedes onclose; let onclose drive the retry
      emitSignalEvent('socket-error', { url: this.wsApiUrl, phase: 'reconnect' });
    };

    ws.onmessage = (event) => {
      const parsed = this.parseInbound(event.data, 'reconnect');
      // The reconnect handshake has its own onmessage, so the module sink must be called from BOTH
      // assignments — this is the one that is live during the outage worth recording.
      emitSignalFrame('in', parsed, {
        url: this.wsApiUrl,
        raw: typeof event.data === 'string' ? event.data : undefined,
      });
      const { action, data } = parsed;
      if (action === EConnectionEvents.HandshakeComplete) {
        this.wsClient = ws;
        if (this.isIntentionalDisconnect) {
          ws.close();
          return;
        }
        logger.log('[SignalerClient] Reconnecting with new connectionId:', data.connectionId);
        // Wire up normal message handling (and the onclose reconnect guard)
        this.setRawListeners();
        this.reconnectAttempts = 0;
        this.hasGivenUp = false;
        this.startHeartbeat();
        // A reconnect rebuilds all peer state under a NEW connectionId — anything queued before
        // it is stale, and replaying old PeerSignals would manufacture glare. Drop, never flush.
        this.dropOutboundQueue('reconnect reset — peer state was rebuilt');
        emitSignalEvent('reconnected', { url: this.wsApiUrl, connectionId: data.connectionId });
        // Notify application layer so it can refresh peer state with the new connectionId
        this.callListener(EWebsocketClientEvents.WsClientReconnected, data);
      }
    };
  }
}
